import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Plus, Building2 } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Button,
  Card,
  Checkbox,
  Drawer,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  StatusPill,
  Table,
  useToast,
} from '../components';
import { PageHeader } from '../layouts/PageHeader';
import { useT } from '../i18n';
import type { Integrator } from '../types/api';
import styles from './Integrators.module.css';

interface CreateIntegratorPayload {
  name: string;
  contactEmail: string;
  contactPhone: string;
  priority: number;
  specialization: string;
  active: boolean;
}

const emptyForm: CreateIntegratorPayload = {
  name: '',
  contactEmail: '',
  contactPhone: '',
  priority: 0,
  specialization: '',
  active: true,
};

export function IntegratorsPage() {
  const { user } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isManufacturer = user?.role === 'MANUFACTURER';

  // Route is manufacturer-only (gated in router.tsx), but we don't want a
  // broken page if anyone bypasses the gate — render a quiet placeholder.
  if (!isManufacturer) {
    return (
      <div className={styles.page}>
        <PageHeader title={t('integrators.title')} />
        <Card>
          <EmptyState
            icon={<Building2 size={28} />}
            title={t('integrators.notAvailableTitle')}
            description={t('integrators.notAvailableDesc')}
          />
        </Card>
      </div>
    );
  }

  return <IntegratorsContent
    isManufacturer={isManufacturer}
    toast={toast}
    queryClient={queryClient}
  />;
}

// Split so hooks can run after the early-return guard above.
function IntegratorsContent({
  isManufacturer,
  toast,
  queryClient,
}: {
  isManufacturer: boolean;
  toast: ReturnType<typeof useToast>;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const { t } = useT();
  // ── Data ─────────────────────────────────────────────────────────
  const integratorsQ = useQuery<Integrator[]>({
    queryKey: ['integrators'],
    queryFn: () => api.get<Integrator[]>('/integrators'),
  });
  const integrators = integratorsQ.data ?? [];

  // ── Detail drawer (supports ?id= deep-link from EntityLink) ──────
  const [searchParams, setSearchParams] = useSearchParams();
  const idFromUrl = searchParams.get('id');
  const [selectedId, setSelectedId] = useState<string | null>(idFromUrl);

  useEffect(() => {
    setSelectedId(idFromUrl);
  }, [idFromUrl]);

  const openIntegrator = (id: string) => {
    setSelectedId(id);
    setSearchParams({ id }, { replace: true });
  };

  const closeIntegrator = () => {
    setSelectedId(null);
    if (searchParams.has('id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  };

  const selected = selectedId ? integrators.find((i) => i.id === selectedId) ?? null : null;

  // ── Create flow ──────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateIntegratorPayload>(emptyForm);
  const updateField = <K extends keyof CreateIntegratorPayload>(
    k: K, v: CreateIntegratorPayload[K],
  ) => setForm((prev) => ({ ...prev, [k]: v }));

  const createMutation = useMutation({
    mutationFn: (payload: CreateIntegratorPayload) => {
      const clean: Record<string, unknown> = {
        name: payload.name.trim(),
        contactEmail: payload.contactEmail.trim(),
        contactPhone: payload.contactPhone.trim(),
        priority: payload.priority || 0,
        active: payload.active,
      };
      if (payload.specialization.trim()) clean.specialization = payload.specialization.trim();
      return api.post<Integrator>('/integrators', clean);
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['integrators'] });
      toast.success(t('integrators.toastCreated'), created.name);
      setCreateOpen(false);
      setForm(emptyForm);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('integrators.errorCreate');
      toast.error(t('integrators.toastCreateFail'), msg);
    },
  });

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (createMutation.isPending) return;
    createMutation.mutate(form);
  };

  const requiredOk =
    form.name.trim() && form.contactEmail.trim() && form.contactPhone.trim();

  const errorMsg = integratorsQ.error instanceof Error ? integratorsQ.error.message : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('integrators.title')}
        description={t('integrators.description')}
        action={
          <Button leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
            {t('integrators.add')}
          </Button>
        }
      />

      {errorMsg && (
        <Card className={styles.error}>
          <p>{errorMsg}</p>
        </Card>
      )}

      {integratorsQ.isLoading ? (
        <IntegratorsSkeleton />
      ) : integrators.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Building2 size={28} />}
            title={t('integrators.emptyTitle')}
            description={t('integrators.emptyDesc')}
            action={
              <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
                {t('integrators.add')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t('integrators.colName')}</th>
              <th>{t('integrators.colEmail')}</th>
              <th>{t('integrators.colPhone')}</th>
              <th className="numeric">{t('integrators.colPriority')}</th>
              <th>{t('integrators.colSpecialization')}</th>
              <th>{t('integrators.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {integrators.map((i) => (
              <tr key={i.id}>
                <td className={styles.name}>
                  <button
                    type="button"
                    className={styles.nameLink}
                    onClick={() => openIntegrator(i.id)}
                  >
                    {i.name}
                  </button>
                </td>
                <td>{i.contactEmail}</td>
                <td>{i.contactPhone}</td>
                <td className="numeric">{i.priority}</td>
                <td>{i.specialization ?? <span className={styles.muted}>—</span>}</td>
                <td>
                  <StatusPill tone={i.active ? 'success' : 'neutral'} subtle={!i.active}>
                    {i.active ? t('common.active') : t('common.inactive')}
                  </StatusPill>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* ── Detail drawer ───────────────────────────────────────── */}
      <Drawer
        open={Boolean(selected)}
        onClose={closeIntegrator}
        title={selected?.name ?? ''}
        description={selected?.specialization ?? undefined}
        size="md"
        footer={
          <Button variant="secondary" onClick={closeIntegrator}>
            {t('common.close')}
          </Button>
        }
      >
        {selected && <IntegratorDetail integrator={selected} />}
      </Drawer>

      {/* ── Create modal ────────────────────────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        title={t('integrators.modalTitle')}
        description={t('integrators.modalDesc')}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCreateOpen(false)}
              disabled={createMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="create-integrator-form"
              loading={createMutation.isPending}
              disabled={!requiredOk}
            >
              {t('common.create')}
            </Button>
          </>
        }
      >
        <form id="create-integrator-form" onSubmit={submitCreate} className={styles.form} noValidate>
          <Input
            label={t('integrators.fieldName')}
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <Input
            label={t('integrators.fieldContactEmail')}
            type="email"
            value={form.contactEmail}
            onChange={(e) => updateField('contactEmail', e.target.value)}
            required
            fullWidth
          />
          <Input
            label={t('integrators.fieldContactPhone')}
            value={form.contactPhone}
            onChange={(e) => updateField('contactPhone', e.target.value)}
            required
            fullWidth
          />
          <Input
            label={t('integrators.fieldPriority')}
            type="number"
            value={String(form.priority)}
            onChange={(e) => updateField('priority', Number.parseInt(e.target.value, 10) || 0)}
            helperText={t('integrators.priorityHelper')}
            fullWidth
          />
          <div className={styles.fullWidth}>
            <Input
              label={t('integrators.fieldSpecialization')}
              value={form.specialization}
              onChange={(e) => updateField('specialization', e.target.value)}
              placeholder={t('integrators.specializationPlaceholder')}
              fullWidth
            />
          </div>
          <div className={styles.fullWidth}>
            <Checkbox
              label={t('integrators.activeLabel')}
              checked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}

function IntegratorDetail({ integrator }: { integrator: Integrator }) {
  const { t } = useT();
  return (
    <div className={styles.detail}>
      <section className={styles.detailGrid}>
        <div className={styles.detailField}>
          <div className={styles.detailLabel}>{t('integrators.colEmail')}</div>
          <div className={styles.detailValue}>{integrator.contactEmail}</div>
        </div>
        <div className={styles.detailField}>
          <div className={styles.detailLabel}>{t('integrators.colPhone')}</div>
          <div className={styles.detailValue}>{integrator.contactPhone}</div>
        </div>
        <div className={styles.detailField}>
          <div className={styles.detailLabel}>{t('integrators.colPriority')}</div>
          <div className={styles.detailValue}>{integrator.priority}</div>
        </div>
        <div className={styles.detailField}>
          <div className={styles.detailLabel}>{t('integrators.colStatus')}</div>
          <div className={styles.detailValue}>
            <StatusPill tone={integrator.active ? 'success' : 'neutral'} subtle={!integrator.active}>
              {integrator.active ? t('common.active') : t('common.inactive')}
            </StatusPill>
          </div>
        </div>
        <div className={styles.detailField} style={{ gridColumn: '1 / -1' }}>
          <div className={styles.detailLabel}>{t('integrators.colSpecialization')}</div>
          <div className={styles.detailValue}>{integrator.specialization ?? '—'}</div>
        </div>
      </section>
    </div>
  );
}

function IntegratorsSkeleton() {
  const { t } = useT();
  return (
    <Table>
      <thead>
        <tr>
          <th>{t('integrators.colName')}</th>
          <th>{t('integrators.colEmail')}</th>
          <th>{t('integrators.colPhone')}</th>
          <th className="numeric">{t('integrators.colPriority')}</th>
          <th>{t('integrators.colSpecialization')}</th>
          <th>{t('integrators.colStatus')}</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 4 }).map((_, i) => (
          <tr key={i}>
            {Array.from({ length: 6 }).map((__, j) => (
              <td key={j}><Skeleton width="80%" /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
